import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import internationalTransferService from '../services/internationalTransferService';
import Card from '../components/common/Card';

const InternationalTransferVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get transfer ID from location state or localStorage
  const transferIdFromState = location.state?.transferId;
  const [transferId, setTransferId] = useState(transferIdFromState || localStorage.getItem('currentTransferId') || null);

  const [transfer, setTransfer] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [code, setCode] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const statusCheckIntervalRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    
    const initializeComponent = async () => {
      // Fetch verification status on component mount
      try {
        const status = await internationalTransferService.getVerificationStatus();
        if (isMounted) {
          setVerificationStatus(status);

          // Update current step from verification status
          if (status.next_required_step && !status.is_verification_completed) {
            setCurrentStep(status.next_required_step);
          }

          // If user is already verified, show success message
          if (status.is_verification_completed) {
            setSuccess('✅ Your account is already fully verified!');
          }
        }
      } catch (error) {
        console.error('Error fetching verification status:', error);
        if (isMounted) {
          setError('Unable to fetch verification status');
        }
      }

      // If we have a transfer ID, fetch transfer details
      if (transferId && isMounted) {
        fetchTransferDetails();
        
        // Check status every 30 seconds
        statusCheckIntervalRef.current = setInterval(fetchTransferStatus, 30000);
      }
    };

    initializeComponent();

    return () => {
      isMounted = false;
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
        statusCheckIntervalRef.current = null;
      }
    };
  }, [transferId]);

  const fetchTransferDetails = async () => {
    try {
      setLoading(true);
      const data = await internationalTransferService.getInternationalTransferDetail(transferId);
      setTransfer(data);
      setCurrentStep(data.current_step || 1);
    } catch (err) {
      console.error('Error fetching transfer details:', err);
      // It's okay if no transfer is found - user might be doing account verification
      if (err.response?.status !== 404) {
        setError(err.response?.data?.error || 'Failed to load transfer details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const status = await internationalTransferService.getVerificationStatus();
      setVerificationStatus(status);

      // Update current step from verification status
      if (status.next_required_step && !status.is_verification_completed) {
        setCurrentStep(status.next_required_step);
      }

      // If user is already verified, show success message
      if (status.is_verification_completed) {
        setSuccess('✅ Your account is already fully verified!');
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
      setError('Unable to fetch verification status');
    }
  };

  const fetchTransferStatus = async () => {
    if (!transferId) return;

    try {
      const data = await internationalTransferService.getInternationalTransferStatus(transferId);
      setTransfer(data);

      if (data.status === 'COMPLETED' || data.status === 'FAILED' || data.status === 'CANCELLED') {
        if (statusCheckIntervalRef.current) {
          clearInterval(statusCheckIntervalRef.current);
          statusCheckIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(`Please enter ${getVerificationStepName(currentStep)}`);
      return;
    }

    try {
      setVerifying(true);
      setError('');
      setSuccess('');

      console.log(`Verifying code: ${code} for step ${currentStep}`);

      // Use the verification endpoint
      const result = await internationalTransferService.verifyAccountStep(code);

      if (result.success) {
        const successMessage = result.is_fully_verified 
          ? '🎉 CONGRATULATIONS! All 5 verification steps completed. Your account is now fully verified for life!'
          : `✅ ${getVerificationStepName(currentStep)} verified!`;
        
        setSuccess(successMessage);
        setCode('');

        // Update step
        if (result.next_step) {
          setCurrentStep(result.next_step);
        }

        // Refresh verification status
        await fetchVerificationStatus();

        // If verification is complete
        if (result.is_fully_verified) {
          // If we have a transfer, continue with it
          if (transferId) {
            setTimeout(() => {
              navigate(`/transfer/international/${transferId}`);
            }, 3000);
          } else {
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          }
        }
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Handle manual verification without a transfer
  const handleAccountVerificationOnly = async (e) => {
    e.preventDefault();
    await handleVerifyCode(e);
  };

  const formatStatus = (status) => {
    const statusMap = {
      'INITIATED': 'Initiated',
      'AWAITING_VERIFICATION': 'Awaiting Verification',
      'VERIFICATION_COMPLETED': 'Verification Completed',
      'PROCESSING': 'Processing',
      'COMPLETED': 'Completed',
      'FAILED': 'Failed',
      'CANCELLED': 'Cancelled',
      'AWAITING_CODE_1': 'Awaiting COT Code',
      'AWAITING_CODE_2': 'Awaiting REV Code',
      'AWAITING_CODE_3': 'Awaiting TAX Code',
      'AWAITING_CODE_4': 'Awaiting NRP Code',
      'AWAITING_CODE_5': 'Awaiting IMF Code',
    };
    return statusMap[status] || status;
  };

  // Get step names for each verification step
  const getVerificationStepName = (stepNumber) => {
    const stepNames = {
      1: 'COT Code',
      2: 'REV Code',
      3: 'TAX Code',
      4: 'NRP Code',
      5: 'IMF Code'
    };
    return stepNames[stepNumber] || `Code ${stepNumber}`;
  };

  const getStatusColor = (status) => {
    if (status === 'COMPLETED') return 'bg-green-100 text-green-800';
    if (status.includes('FAILED') || status === 'CANCELLED') return 'bg-red-100 text-red-800';
    if (status.includes('AWAITING') || status === 'PROCESSING') return 'bg-yellow-100 text-yellow-800';
    if (status === 'VERIFICATION_COMPLETED') return 'bg-blue-100 text-blue-800';
    return 'bg-neutral-100 text-neutral-800';
  };

  const checkAccountVerificationStatus = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await fetchVerificationStatus();
      setSuccess('Verification status updated successfully');
    } catch (error) {
      console.error('Error checking verification status:', error);
      setError('Unable to fetch verification status');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCodes = () => {
    setError('');
    setSuccess('');
    setCode('');
  };

  if (loading && !verificationStatus) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading verification details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-neutral-900 mb-2">
              {verificationStatus?.is_verification_completed ? '✅ Account Verified' : 'Account Verification'}
            </h1>
            <p className="text-neutral-600">
              {verificationStatus?.is_verification_completed
                ? 'Your account is fully verified for life!'
                : ''
              }
              {transferId && ` for Transfer #${transferId}`}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Transfer Details (Only if we have a transfer) */}
            {transfer ? (
              <div className="lg:col-span-2 space-y-6">
                {/* Transfer Details Card */}
                <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-8">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-neutral-900">Transfer Details</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(transfer.status)}`}>
                      {formatStatus(transfer.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-3">Sender Information</h3>
                      <div className="space-y-2">
                        <p className="text-sm">
                          <span className="text-neutral-600">Account:</span>{' '}
                          <span className="font-semibold">{transfer.from_account_number}</span>
                        </p>
                        <p className="text-sm">
                          <span className="text-neutral-600">Balance:</span>{' '}
                          <span className="font-semibold">${transfer.account_balance} {transfer.account_currency}</span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-3">Recipient Information</h3>
                      <div className="space-y-2">
                        <p className="text-sm">
                          <span className="text-neutral-600">Name:</span>{' '}
                          <span className="font-semibold">{transfer.to_account_name}</span>
                        </p>
                        <p className="text-sm">
                          <span className="text-neutral-600">Account:</span>{' '}
                          <span className="font-semibold">{transfer.to_account_number}</span>
                        </p>
                        <p className="text-sm">
                          <span className="text-neutral-600">Bank:</span>{' '}
                          <span className="font-semibold">{transfer.to_bank_name}, {transfer.to_bank_country}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-neutral-200 pt-8">
                    <h3 className="font-semibold text-neutral-900 mb-4">Transfer Amount</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-neutral-50 rounded-xl">
                        <p className="text-sm text-neutral-600">Amount</p>
                        <p className="text-2xl font-bold text-neutral-900">${transfer.amount}</p>
                      </div>
                      <div className="text-center p-4 bg-neutral-50 rounded-xl">
                        <p className="text-sm text-neutral-600">Fees</p>
                        <p className="text-2xl font-bold text-neutral-900">${transfer.fees || '0.00'}</p>
                      </div>
                      <div className="text-center p-4 bg-primary-50 rounded-xl">
                        <p className="text-sm text-primary-600">Total</p>
                        <p className="text-2xl font-bold text-primary-600">${transfer.total_amount || transfer.amount}</p>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Verification Progress Card */}
                {verificationStatus && !verificationStatus.is_verification_completed && (
                  <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-8">
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-neutral-900 mb-4">Verification Progress</h3>

                      {/* Progress Bar */}
                      <div className="mb-8">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium text-neutral-700">
                            Step {verificationStatus.next_required_step || currentStep} of 5
                          </span>
                          <span className="text-sm font-medium text-primary-600">
                            {verificationStatus.progress_percentage || 0}% Complete
                          </span>
                        </div>
                        <div className="w-full bg-neutral-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-primary-500 to-accent-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${verificationStatus.progress_percentage || 0}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Steps Visualization */}
                      <div className="flex justify-between mb-6">
                        {[1, 2, 3, 4, 5].map((stepNum) => (
                          <div key={stepNum} className="flex flex-col items-center">
                            <div className={`
                              w-12 h-12 rounded-full flex items-center justify-center mb-2 border-2
                              ${verificationStatus.completed_steps && verificationStatus.completed_steps.includes(stepNum)
                                ? 'bg-green-500 border-green-600 text-white'
                                : stepNum === (verificationStatus.next_required_step || currentStep)
                                ? 'bg-primary-500 border-primary-600 text-white'
                                : 'bg-white border-neutral-300 text-neutral-500'
                              }
                            `}>
                              {verificationStatus.completed_steps && verificationStatus.completed_steps.includes(stepNum) ? (
                                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <span className="font-bold text-lg">{stepNum}</span>
                              )}
                            </div>
                            <span className="text-xs font-medium text-neutral-700">
                              Step {stepNum}: {getVerificationStepName(stepNum)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Status Info */}
                      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <p className="text-primary-800 font-medium">
                              {(!verificationStatus.completed_steps || verificationStatus.completed_steps.length === 0)
                                ? 'Start your verification journey'
                                : verificationStatus.completed_steps.length === 5
                                ? '🎉 Verification Complete!'
                                : `Step ${verificationStatus.next_required_step || currentStep} of 5: ${getVerificationStepName(verificationStatus.next_required_step || currentStep)}`
                              }
                            </p>
                            <p className="text-primary-600 text-sm mt-1">
                              {verificationStatus.completed_steps ? verificationStatus.completed_steps.length : 0} steps completed, 
                              {verificationStatus.remaining_steps ? verificationStatus.remaining_steps.length : 5} remaining
                            </p>
                            {verificationStatus.has_active_code && (
                              <p className="text-green-600 text-sm mt-1">
                                ✓ You have an active verification code for this step
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            ) : (
              /* If no transfer, show account verification info */
              <div className="lg:col-span-2 space-y-6">
               
              </div>
            )}

            {/* Right Column - Verification Form */}
            <div className="space-y-6">
              {/* Verification Form Card */}
              <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 sticky top-8">
                <h3 className="text-xl font-bold text-neutral-900 mb-6 text-center">
                  {verificationStatus?.is_verification_completed
                    ? '✅ Account Verified'
                    : `Enter ${getVerificationStepName(currentStep)}`
                  }
                </h3>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="flex-1">{error}</span>
                    <button
                      onClick={handleClearCodes}
                      className="ml-2 text-red-600 hover:text-red-800"
                      aria-label="Clear error"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}

                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="flex-1">{success}</span>
                    <button
                      onClick={handleClearCodes}
                      className="ml-2 text-green-600 hover:text-green-800"
                      aria-label="Clear message"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}

                {verificationStatus?.is_verification_completed ? (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="text-2xl font-bold text-green-700 mb-2">🎉 Fully Verified!</h4>
                    <p className="text-green-600 mb-4">
                      Your account is verified for life!
                    </p>
                    <p className="text-sm text-neutral-600 mb-6">
                      You can now make domestic and international transfers without verification codes.
                    </p>
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-accent-700 transition"
                      >
                        Go to Dashboard
                      </button>
                      <button
                        onClick={() => navigate('/transactions')}
                        className="w-full bg-white text-neutral-700 py-3 rounded-xl border-2 border-neutral-200 font-semibold hover:border-neutral-300 transition"
                      >
                        View Transactions
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleVerifyCode}>
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-neutral-700 mb-3">
                          {getVerificationStepName(currentStep)}
                        </label>
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => {
                            setCode(e.target.value.toUpperCase());
                            setError('');
                            setSuccess('');
                          }}
                          placeholder={`Enter ${getVerificationStepName(currentStep)}`}
                          maxLength="6"
                          className="w-full px-4 py-3 text-center text-2xl font-mono border-2 border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-900 uppercase tracking-widest"
                          autoComplete="off"
                          autoFocus
                        />
                        <p className="text-sm text-neutral-500 mt-2 text-center">
                        
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={verifying || !code.trim()}
                        className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white py-4 rounded-xl hover:from-primary-700 hover:to-accent-700 transition transform hover:scale-105 disabled:from-neutral-300 disabled:to-neutral-400 disabled:cursor-not-allowed disabled:transform-none font-semibold text-lg shadow-lg mb-4"
                      >
                        {verifying ? (
                          <div className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Verifying {getVerificationStepName(currentStep)}...
                          </div>
                        ) : (
                          `Verify ${getVerificationStepName(currentStep)}`
                        )}
                      </button>
                    </form>
                  </>
                )}

                {/* Important Notes */}
                <div className="mt-6 pt-6 border-t border-neutral-200">     
                  <ul className="text-sm text-neutral-600 space-y-2">
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-primary-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Need help? Contact bank admin: <strong>omegatrbnk01@aol.com</strong></span>
                    </li>
                  </ul>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternationalTransferVerification;
